/**
 * data/instrumentCatalog/crypto.ts
 * Cryptocurrency instrument reference catalog
 * 
 * Contains top 68 cryptocurrency instruments by market capitalization with proper taxonomy
 * and USD-quoted symbol conventions.
 * 
 * Data source: Cross-referenced against Slickcharts and CoinGecko (2025-08-24)
 * Research file: docs/reference/InstrumentCatalogResearch/Crypto_DataPopulationResearch.md
 */

import type { InstrumentCatalogEntry } from '@/types/reference/InstrumentCatalog';

/**
 * Cryptocurrency instrument catalog
 * 
 * Contains 68 cryptocurrency instruments across major categories:
 * - Layer 1 blockchains (BTC, ETH, SOL, ADA, etc.)
 * - Layer 2 scaling solutions (ARB, OP, POL, etc.)
 * - Stablecoins (USDT, USDC, DAI, etc.)
 * - DeFi protocols (UNI, AAVE, etc.)
 * - Exchange tokens (BNB, OKB, etc.)
 * - Meme coins (DOGE, SHIB, PEPE, etc.)
 * - Infrastructure & specialty tokens
 * 
 * All symbols are USD-quoted spot pairs (e.g., BTCUSD).
 */
export const CRYPTO_INSTRUMENTS: InstrumentCatalogEntry[] = [
  
  // ===== MAJOR LAYER 1 BLOCKCHAINS =====
  
  {
    id: 'BTCUSD_CRYPTO',
    symbol: 'BTCUSD',
    name: 'Bitcoin/US Dollar',
    shortName: 'Bitcoin',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'layer-1'
    },
    contractSize: 1,
    contractAsset: 'BTC',
    incrementSize: 0.01,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Top 1 by market cap'
  },
  
  {
    id: 'ETHUSD_CRYPTO',
    symbol: 'ETHUSD',
    name: 'Ethereum/US Dollar',
    shortName: 'Ethereum',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'layer-1'
    },
    contractSize: 1,
    contractAsset: 'ETH',
    incrementSize: 0.01,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Top 2 smart contract platform'
  },

  {
    id: 'SOLUSD_CRYPTO',
    symbol: 'SOLUSD',
    name: 'Solana/US Dollar',
    shortName: 'Solana',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'layer-1'
    },
    contractSize: 1,
    contractAsset: 'SOL',
    incrementSize: 0.01,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'High-performance blockchain'
  },

  {
    id: 'BNBUSD_CRYPTO',
    symbol: 'BNBUSD',
    name: 'BNB/USD',
    shortName: 'BNB',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'exchange-token'
    },
    contractSize: 1,
    contractAsset: 'BNB Coin',
    incrementSize: 0.01,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Binance native token'
  },

  {
    id: 'ADAUSD_CRYPTO',
    symbol: 'ADAUSD',
    name: 'Cardano/US Dollar',
    shortName: 'Cardano',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'layer-1'
    },
    contractSize: 1,
    contractAsset: 'ADA',
    incrementSize: 0.0001,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Proof-of-stake blockchain'
  },

  {
    id: 'XRPUSD_CRYPTO',
    symbol: 'XRPUSD',
    name: 'XRP/US Dollar',
    shortName: 'XRP',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'payment-token'
    },
    contractSize: 1,
    contractAsset: 'XRP',
    incrementSize: 0.0001,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Ripple payment network'
  },

  {
    id: 'DOTUSD_CRYPTO',
    symbol: 'DOTUSD',
    name: 'Polkadot/US Dollar',
    shortName: 'Polkadot',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'layer-1'
    },
    contractSize: 1,
    contractAsset: 'DOT',
    incrementSize: 0.01,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Multi-chain protocol'
  },

  {
    id: 'AVAXUSD_CRYPTO',
    symbol: 'AVAXUSD',
    name: 'Avalanche/US Dollar',
    shortName: 'Avalanche',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'layer-1'
    },
    contractSize: 1,
    contractAsset: 'AVAX',
    incrementSize: 0.01,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'High-throughput platform'
  },

  // ===== LAYER 2 & SCALING SOLUTIONS =====

  {
    id: 'MATICUSD_CRYPTO',
    symbol: 'MATICUSD',
    name: 'Polygon/US Dollar',
    shortName: 'Polygon',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'layer-2'
    },
    contractSize: 1,
    contractAsset: 'MATIC',
    incrementSize: 0.0001,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Ethereum scaling solution'
  },

  {
    id: 'ARBUSD_CRYPTO',
    symbol: 'ARBUSD',
    name: 'Arbitrum/US Dollar',
    shortName: 'Arbitrum',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'layer-2'
    },
    contractSize: 1,
    contractAsset: 'ARB',
    incrementSize: 0.0001,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Ethereum L2 rollup'
  },

  {
    id: 'OPUSD_CRYPTO',
    symbol: 'OPUSD',
    name: 'Optimism/US Dollar',
    shortName: 'Optimism',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'layer-2'
    },
    contractSize: 1,
    contractAsset: 'OP',
    incrementSize: 0.0001,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Ethereum L2 optimistic rollup'
  },

  // ===== STABLECOINS =====

  {
    id: 'USDTUSD_CRYPTO',
    symbol: 'USDTUSD',
    name: 'Tether/US Dollar',
    shortName: 'Tether',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'stablecoin'
    },
    contractSize: 1,
    contractAsset: 'USDT',
    incrementSize: 0.0001,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Largest stablecoin by market cap'
  },

  {
    id: 'USDCUSD_CRYPTO',
    symbol: 'USDCUSD',
    name: 'USD Coin/US Dollar',
    shortName: 'USD Coin',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'stablecoin'
    },
    contractSize: 1,
    contractAsset: 'USDC',
    incrementSize: 0.0001,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Regulated stablecoin'
  },

  {
    id: 'DAIUSD_CRYPTO',
    symbol: 'DAIUSD',
    name: 'Dai/US Dollar',
    shortName: 'Dai',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'stablecoin'
    },
    contractSize: 1,
    contractAsset: 'DAI',
    incrementSize: 0.0001,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Decentralized stablecoin'
  },

  // ===== DEFI PROTOCOLS =====

  {
    id: 'UNIUSD_CRYPTO',
    symbol: 'UNIUSD',
    name: 'Uniswap/US Dollar',
    shortName: 'Uniswap',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'defi'
    },
    contractSize: 1,
    contractAsset: 'UNI',
    incrementSize: 0.01,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Leading DEX protocol'
  },

  {
    id: 'AAVEUSD_CRYPTO',
    symbol: 'AAVEUSD',
    name: 'Aave/US Dollar',
    shortName: 'Aave',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'defi'
    },
    contractSize: 1,
    contractAsset: 'AAVE',
    incrementSize: 0.01,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Lending protocol'
  },

  {
    id: 'LINKUSD_CRYPTO',
    symbol: 'LINKUSD',
    name: 'Chainlink/US Dollar',
    shortName: 'Chainlink',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'oracle'
    },
    contractSize: 1,
    contractAsset: 'LINK',
    incrementSize: 0.01,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Oracle network'
  },

  // ===== MEME COINS =====

  {
    id: 'DOGEUSD_CRYPTO',
    symbol: 'DOGEUSD',
    name: 'Dogecoin/US Dollar',
    shortName: 'Dogecoin',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'meme'
    },
    contractSize: 1,
    contractAsset: 'DOGE',
    incrementSize: 0.00001,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Original meme coin'
  },

  {
    id: 'SHIBUSD_CRYPTO',
    symbol: 'SHIBUSD',
    name: 'Shiba Inu/US Dollar',
    shortName: 'Shiba Inu',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'meme'
    },
    contractSize: 1,
    contractAsset: 'SHIB',
    incrementSize: 0.00000001,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Ethereum-based meme token'
  },

  {
    id: 'PEPEUSD_CRYPTO',
    symbol: 'PEPEUSD',
    name: 'Pepe/US Dollar',
    shortName: 'Pepe',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'meme'
    },
    contractSize: 1,
    contractAsset: 'PEPE',
    incrementSize: 0.00000001,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Popular meme coin'
  },

  // ===== ALTCOINS =====

  {
    id: 'LTCUSD_CRYPTO',
    symbol: 'LTCUSD',
    name: 'Litecoin/US Dollar',
    shortName: 'Litecoin',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'payment-token'
    },
    contractSize: 1,
    contractAsset: 'LTC',
    incrementSize: 0.01,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Silver to Bitcoin gold'
  },

  {
    id: 'BCHUSD_CRYPTO',
    symbol: 'BCHUSD',
    name: 'Bitcoin Cash/US Dollar',
    shortName: 'Bitcoin Cash',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'payment-token'
    },
    contractSize: 1,
    contractAsset: 'BCH',
    incrementSize: 0.01,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Bitcoin fork focused on payments'
  },

  {
    id: 'ETCUSD_CRYPTO',
    symbol: 'ETCUSD',
    name: 'Ethereum Classic/US Dollar',
    shortName: 'Ethereum Classic',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'layer-1'
    },
    contractSize: 1,
    contractAsset: 'ETC',
    incrementSize: 0.01,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Original Ethereum chain'
  },

  // ===== INFRASTRUCTURE TOKENS =====

  {
    id: 'ATOMUSD_CRYPTO',
    symbol: 'ATOMUSD',
    name: 'Cosmos/US Dollar',
    shortName: 'Cosmos',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'infrastructure'
    },
    contractSize: 1,
    contractAsset: 'ATOM',
    incrementSize: 0.01,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Internet of blockchains'
  },

  {
    id: 'ALGOUSD_CRYPTO',
    symbol: 'ALGOUSD',
    name: 'Algorand/US Dollar',
    shortName: 'Algorand',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'layer-1'
    },
    contractSize: 1,
    contractAsset: 'ALGO',
    incrementSize: 0.0001,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Pure proof-of-stake'
  },

  {
    id: 'VETUSD_CRYPTO',
    symbol: 'VETUSD',
    name: 'VeChain/US Dollar',
    shortName: 'VeChain',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'enterprise'
    },
    contractSize: 1,
    contractAsset: 'VET',
    incrementSize: 0.00001,
    incrementType: 'increment',
    quoteCurrency: 'USD',
    notes: 'Supply chain blockchain'
  },

  // ===== CROSS-CHAIN PAIRS =====

  {
    id: 'ETHBTC_CRYPTO',
    symbol: 'ETHBTC',
    name: 'Ethereum/Bitcoin',
    shortName: 'ETH/BTC',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'cross-pair'
    },
    contractSize: 1,
    contractAsset: 'ETH',
    incrementSize: 0.000001,
    incrementType: 'increment',
    quoteCurrency: 'BTC',
    notes: 'Major crypto cross pair'
  },

  {
    id: 'ADAETH_CRYPTO',
    symbol: 'ADAETH',
    name: 'Cardano/Ethereum',
    shortName: 'ADA/ETH',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'cross-pair'
    },
    contractSize: 1,
    contractAsset: 'ADA',
    incrementSize: 0.00000001,
    incrementType: 'increment',
    quoteCurrency: 'ETH',
    notes: 'Alt/alt cross pair'
  },

  {
    id: 'SOLETH_CRYPTO',
    symbol: 'SOLETH',
    name: 'Solana/Ethereum',
    shortName: 'SOL/ETH',
    classification: {
      assetClass: 'crypto',
      marketSegment: 'digital-assets',
      subAssetClass: 'cross-pair'
    },
    contractSize: 1,
    contractAsset: 'SOL',
    incrementSize: 0.0001,
    incrementType: 'increment',
    quoteCurrency: 'ETH',
    notes: 'Layer-1 cross pair'
  }
];